export function truncate(inputString: string, length: number, suffix = '…') {
  const trimmedString = inputString.trim();
  return trimmedString.length > length
    ? `${trimmedString.substring(0, length)}${suffix}`
    : inputString;
}

export function reverseTruncate(
  inputString: string,
  length: number,
  prefix = '…',
) {
  const trimmedString = inputString.trim();
  const stringLength = trimmedString.length;
  return stringLength > length
    ? `${prefix}${trimmedString.substring(stringLength - length, stringLength)}`
    : inputString;
}

export function middleTruncate(
  inputString: string,
  length: number,
  prefix = '…',
) {
  const trimmedString = inputString.trim();
  const stringLength = trimmedString.length;
  const prefixLength = prefix.length;
  if (stringLength <= length) return trimmedString;
  const trimLength = (length - prefixLength) / 2;
  const frontChars = Math.ceil(trimLength);
  const backChars = Math.floor(trimLength);
  return (
    trimmedString.substr(0, frontChars) +
    prefix +
    trimmedString.substr(trimmedString.length - backChars)
  );
}

const GROUPING_CHARACTER_REGEX = /'|"|\(|\)|\[|\]/g;
const NON_WORD_CHARACTERS_REGEX = /\W+/g;
const LEADING_OR_TRAILING_DASHES_REGEX = /^-+|-+$/g;

export function handleize(inputString: string) {
  return inputString
    .toLowerCase()
    .replace(GROUPING_CHARACTER_REGEX, '')
    .replace(NON_WORD_CHARACTERS_REGEX, '-')
    .replace(LEADING_OR_TRAILING_DASHES_REGEX, '');
}

const DASHES_AND_UNDERSCORES_REGEX = /(_|-)([a-z])/g;

export function toCamelCase(inputString: string) {
  return inputString
    .toLowerCase()
    .replace(DASHES_AND_UNDERSCORES_REGEX, (str) => str[1].toUpperCase());
}

export function toPascalCase(value: string) {
  return capitalizeFirstLetter(toCamelCase(value));
}

const HTML_UNESCAPES = {
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
};

const ESCAPED_HTML_REGEX = /&(?:amp|lt|gt|quot|#39);/g;
const MULTIPLE_SPACES_REGEX = /\s+/g;
const HTML_TAG_REGEX = /<[^>]+>/g;
const STYLE_AND_SCRIPT_REGEX =
  /<(style|script)[^<]*>[\s\S]*?<\/(style|script)>/gi;

export function htmlToPlainText(htmlString: string) {
  return htmlString
    .replace(STYLE_AND_SCRIPT_REGEX, '')
    .replace(HTML_TAG_REGEX, '')
    .replace(MULTIPLE_SPACES_REGEX, ' ')
    .replace(ESCAPED_HTML_REGEX, unescapeHTMLEntity)
    .trim();
}

function unescapeHTMLEntity(entity: string) {
  return HTML_UNESCAPES.hasOwnProperty(entity)
    ? HTML_UNESCAPES[entity as keyof typeof HTML_UNESCAPES]
    : entity;
}

export function capitalizeFirstLetter(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

// Native padStart isn't supported in Safari before v10.3
export function padStart(
  input: string | number,
  minLength: number,
  padString = ' ',
) {
  const stringInput = String(input);
  if (stringInput.length > minLength) {
    return input;
  } else {
    const paddingLength = minLength - stringInput.length;
    const repeatedPadding = `${padString.repeat(
      Math.ceil(paddingLength / padString.length),
    )}`;

    const padding =
      paddingLength > padString.length ? repeatedPadding : padString;
    return `${padding.slice(0, paddingLength)}${input}`;
  }
}

export function normalizeStringForAWSKey(name: string) {
  return name.replace(/[\s]/gi, '');
}

export function isStringNumerical(str: string): boolean {
  return isFinite(Number(str));
}
